// --- User-tier page visibility (ocn-S184-P0) --------------------
// Single source of truth for which dashboard pages are visible to the
// S182 user_tier (builder | developer). Imported by BOTH the nav
// (components/layout/Sidebar.tsx) and the route gate (app/AppRouter.tsx)
// so they can never drift.
//
// Two distinct tier systems exist in the dashboard:
//   - OnboardingTier (context/OnboardingContext): progressive disclosure
//     new_user..graduated. Already gates nav by progress. Unchanged here.
//   - UserTier (context/AuthContext, S182): builder | developer. THIS module
//     overlays a persona gate on top of the onboarding gate.
//
// Builder = minimal surface (chat home, my app, settings, bootstrap). The
// deep engineering pages (Sync, Logs, RunDetail, Guardian, Infra, Modules,
// Costs, etc.) are hidden + non-navigable for builders. Developer = full
// surface, behavior-preserving (isPageAllowedForTier is always true).

import type { UserTier } from '../context/AuthContext';

/** Builder's home / safe fallback page id. */
export const BUILDER_HOME = 'subscriber-chat';

/**
 * Exact page ids that are DEVELOPER-only (hidden + non-navigable for builders).
 * Everything not listed here (and not matching DEVELOPER_ONLY_PREFIXES) is
 * builder-visible.
 */
export const DEVELOPER_ONLY_PAGES: ReadonlySet<string> = new Set<string>([
  'runs',
  'logs',
  'sync',
  'journal',
  'guardian',
  'skills',
  'modules',
  'infra',
  'costs',
  'safety',
  'credentials',
  'intel',
]);

/**
 * Dynamic page-id prefixes that are DEVELOPER-only. `run:<id>` is the RunDetail
 * deep page (explicitly named deep page; only reachable via the dev-only Logs).
 * The guided credential wizards (`account-setup`, `project-setup:<id>`) are left
 * builder-reachable on purpose: a builder bootstrapping "My App" needs guided
 * setup. The raw `credentials` management page stays developer-only via
 * DEVELOPER_ONLY_PAGES.
 */
export const DEVELOPER_ONLY_PREFIXES: readonly string[] = ['run:'];

/**
 * Is `page` allowed for the given user tier?
 * - developer: always true (full access, behavior-preserving).
 * - builder: true unless the page is in the developer-only set / prefixes.
 */
export function isPageAllowedForTier(page: string, tier: UserTier): boolean {
  if (tier === 'developer') return true;
  if (DEVELOPER_ONLY_PAGES.has(page)) return false;
  if (DEVELOPER_ONLY_PREFIXES.some((prefix: string) => page.startsWith(prefix))) return false;
  return true;
}

/** Product-aware cold-start resolution. Product containment is resolved by the
 * API entitlement helper; this router only consumes its booleans. */
export type LandingDecision =
  | { kind: 'project-chat'; projectId: string }
  | { kind: 'factory-cold-start' }
  | { kind: 'forge-cold-start' };

export function resolveLanding(
  projects: readonly { project_id?: string | null }[] | null | undefined,
  entitlement?: { hasForge?: boolean; hasFactory?: boolean } | null,
): LandingDecision {
  const mostRecentProject = projects?.find((project) => Boolean(project?.project_id));
  if (mostRecentProject?.project_id) return { kind: 'project-chat', projectId: mostRecentProject.project_id };
  // Missing/failed entitlement deliberately falls through to Factory, the safe
  // column-default superset. Do not turn an outage into a white screen.
  if (entitlement?.hasFactory !== false) return { kind: 'factory-cold-start' };
  if (entitlement?.hasForge) return { kind: 'forge-cold-start' };
  return { kind: 'factory-cold-start' };
}
